import { type JSX, useState } from 'react';
import { Alert, Button, Group, Modal, PasswordInput, Stack, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { api, explicarMotivo } from '../lib/api.js';

/**
 * Troca da própria senha. Qualquer papel, a qualquer momento.
 *
 * Pede a senha ATUAL (um cookie roubado não deve poder trancar o dono fora) e
 * avisa que as outras sessões caem: quem troca por desconfiar de acesso
 * indevido não ganha nada se as outras continuarem vivas.
 */
export function TrocarSenha({
  aberto,
  aoFechar,
}: {
  aberto: boolean;
  aoFechar: () => void;
}): JSX.Element {
  const [atual, setAtual] = useState('');
  const [nova, setNova] = useState('');
  const [repetida, setRepetida] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);

  function limpar(): void {
    setAtual('');
    setNova('');
    setRepetida('');
    setErro(null);
  }

  async function trocar(): Promise<void> {
    setSalvando(true);
    try {
      const r = await api.post<{ sessoesRevogadas: number }>('/admin/senha', { atual, nova });
      limpar();
      notifications.show({
        color: 'green',
        title: 'Senha trocada',
        message:
          r.sessoesRevogadas > 0
            ? `${r.sessoesRevogadas} outra(s) sessão(ões) foram encerradas — se você estava logado em outro computador, precisará entrar de novo.`
            : 'Não havia outra sessão aberta.',
        autoClose: 8_000,
      });
      aoFechar();
    } catch (e) {
      setErro(explicarMotivo(e, 'não foi possível trocar a senha'));
    } finally {
      setSalvando(false);
    }
  }

  const naoCoincide = repetida.length > 0 && nova !== repetida;
  const curta = nova.length > 0 && nova.length < 12;
  const pode = atual.length > 0 && nova.length >= 12 && nova === repetida && !salvando;

  return (
    <Modal
      opened={aberto}
      onClose={() => {
        limpar();
        aoFechar();
      }}
      title="Trocar minha senha"
      centered
    >
      <Stack gap="sm">
        <PasswordInput
          label="Senha atual"
          value={atual}
          autoComplete="current-password"
          data-autofocus
          onChange={(e) => setAtual(e.currentTarget.value)}
        />
        <PasswordInput
          label="Nova senha"
          description="Mínimo 12 caracteres"
          value={nova}
          autoComplete="new-password"
          error={curta ? `Faltam ${12 - nova.length} caracteres.` : null}
          onChange={(e) => setNova(e.currentTarget.value)}
        />
        <PasswordInput
          label="Repita a nova senha"
          value={repetida}
          autoComplete="new-password"
          error={naoCoincide ? 'As duas senhas não são iguais.' : null}
          onChange={(e) => setRepetida(e.currentTarget.value)}
        />

        <Text size="xs" c="dimmed">
          Ao trocar, as <strong>outras</strong> sessões são encerradas; esta continua.
        </Text>

        {erro && (
          <Alert color="red" variant="light" role="alert">
            {erro}
          </Alert>
        )}

        <Group justify="flex-end">
          <Button onClick={aoFechar}>Cancelar</Button>
          <Button variant="filled" loading={salvando} disabled={!pode} onClick={() => void trocar()}>
            Trocar senha
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
